import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { UnifiedTable } from '@/components/business/UnifiedTable';
import { loadTeamsWithEloRanking, loadStats } from '@/utils/dataLoader';
import type { Player, Team, HistoricalPlayer } from '@/types';

const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [stats, setStats] = useState<{
    totalTeams?: number;
    lastUpdated?: string;
  }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [teamsData, statsData] = await Promise.all([
          loadTeamsWithEloRanking(),
          loadStats(),
        ]);
        setTeams(teamsData);
        setStats(statsData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载战队数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleTeamClick = (record: Player | Team | HistoricalPlayer) => {
    const team = record as Team;
    navigate(`/team/${team.id}`);
  };

  if (loading) {
    return (
      <Layout currentPath='/teams'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-text-secondary'>加载中...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPath='/teams'>
      <div className='space-y-8'>
        {/* 页面标题 */}
        <div className='text-center'>
          <h1 className='text-3xl font-bold text-text-primary mb-2'>
            战队ELO排名系统
          </h1>
          <p className='text-text-secondary'>
            基于选手平均ELO分数的战队实力排名
          </p>
        </div>

        {/* 战队排名表格 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            战队ELO排名 Top {teams.length}
          </h2>
          <UnifiedTable
            variant='team'
            data={teams}
            onRowClick={handleTeamClick}
            teamNameDate={stats.lastUpdated?.split(' ')[0]}
          />
        </div>

        {/* 统计信息 */}
        <div className='bg-background-secondary rounded-xl p-6 border border-border'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 text-center'>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {stats.lastUpdated?.split(' ')[0] || 'N/A'}
              </div>
              <div className='text-sm text-text-secondary'>最后更新日期</div>
            </div>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {stats.totalTeams || teams.length}
              </div>
              <div className='text-sm text-text-secondary'>活跃战队数量</div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeamsPage;
